// src/pages/VastInspectorPage.jsx
//
// VAST Inspector — paste any VAST/VMAP XML and get a structured breakdown:
// ad system/id/title, wrapper chain, impression/tracking-event inventory,
// media files, and a macro audit (raw scan of the pasted XML for unsubstituted
// [MACRO] / %%MACRO%% tokens).
//
// Talks to the proxy's parse-vast endpoint (proxy-server, parallel PR):
//   POST /proxy/parse-vast  { xml }  -> { ok, parsed }
// `parsed` is the proxy's parseVast.js output — shape not guaranteed here, so
// every section below reads defensively (optional chaining / fallbacks) and
// simply omits itself if the expected keys aren't present.
//
// Same PROXY_API_BASE idiom as api/checks.js / api/appPacks.js / api/agentChat.js:
// REACT_APP_PROXY_API_URL in prod, falls back to a bare "/proxy" path relative
// to the page origin for local dev.

import React, { useMemo, useState, useCallback } from 'react';

const PROXY_API_BASE = (process.env.REACT_APP_PROXY_API_URL || '/proxy').replace(/\/$/, '');

async function parseVastXml(xml) {
  const res = await fetch(`${PROXY_API_BASE}/proxy/parse-vast`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ xml }),
  });
  let body = null;
  try { body = await res.json(); } catch { /* ignore */ }
  if (!res.ok) {
    const msg = (body && (body.detail || body.error)) || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return body;
}

function copyToClipboard(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve, reject) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      ok ? resolve() : reject(new Error('execCommand copy failed'));
    } catch (e) {
      reject(e);
    }
  });
}

function CopyButton({ getText, label = 'Copy parsed JSON' }) {
  const [state, setState] = useState('idle');
  const onClick = useCallback(async () => {
    try {
      await copyToClipboard(getText());
      setState('ok');
    } catch {
      setState('err');
    }
    setTimeout(() => setState('idle'), 1500);
  }, [getText]);
  return (
    <button onClick={onClick} style={btnGhost}>
      {state === 'ok' ? 'Copied' : state === 'err' ? 'Copy failed' : label}
    </button>
  );
}

// Known macro token shapes: [FOO_BAR], %%FOO_BAR%%, and the ${FOO_BAR} form
// some SSPs use. Distinct token text + count of occurrences in the raw XML.
function auditMacros(rawXml) {
  if (!rawXml) return [];
  const counts = new Map();
  const patterns = [
    /\[[A-Z_][A-Z0-9_]*\]/g,       // [TIMESTAMP]
    /%%[A-Z_][A-Z0-9_]*%%/g,       // %%CACHEBUSTER%%
  ];
  for (const re of patterns) {
    const matches = rawXml.match(re) || [];
    for (const m of matches) {
      counts.set(m, (counts.get(m) || 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([token, count]) => ({ token, count }))
    .sort((a, b) => b.count - a.count);
}

// Defensive getters over the unknown `parsed` shape.
function getAds(parsed) {
  if (!parsed) return [];
  if (Array.isArray(parsed.ads)) return parsed.ads;
  if (parsed.ad) return [parsed.ad];
  // Some parsers put a single top-level ad's fields directly on `parsed`.
  if (parsed.adSystem || parsed.adId || parsed.adTitle) return [parsed];
  return [];
}

function getWrapperChain(parsed) {
  if (!parsed) return [];
  return parsed.wrapperChain || parsed.wrappers || parsed.redirects || [];
}

function flattenTrackingEvents(ad) {
  // Accept several plausible shapes: ad.trackingEvents = { event: [urls] }
  // or ad.trackingEvents = [{ event, url }], or ad.trackers similarly.
  const src = ad?.trackingEvents || ad?.trackers || {};
  const grouped = {};
  const bump = (event, url) => {
    const key = event || 'unknown';
    if (!grouped[key]) grouped[key] = [];
    if (url) grouped[key].push(url);
  };
  if (Array.isArray(src)) {
    for (const t of src) bump(t.event || t.type, t.url || t.uri);
  } else if (src && typeof src === 'object') {
    for (const [event, urls] of Object.entries(src)) {
      if (Array.isArray(urls)) urls.forEach((u) => bump(event, typeof u === 'string' ? u : u?.url || u?.uri));
      else bump(event, urls);
    }
  }
  return grouped;
}

function getImpressions(ad) {
  const imp = ad?.impressions || ad?.impression || [];
  if (Array.isArray(imp)) return imp.map((i) => (typeof i === 'string' ? i : i?.url || i?.uri)).filter(Boolean);
  if (typeof imp === 'string') return [imp];
  return [];
}

function getMediaFiles(ad) {
  const mf = ad?.mediaFiles || ad?.media || [];
  if (Array.isArray(mf)) return mf;
  return [];
}

function truncateUrl(url, max = 70) {
  if (!url) return '';
  return url.length > max ? url.slice(0, max) + '…' : url;
}

// embedded: rendered as a tab of InspectPage, which owns the paste box and
// passes its text as `value`; this page then drops its own header and input.
// Parsing stays behind the Parse button either way: it is a proxy round-trip,
// not something to fire on every keystroke.
export default function VastInspectorPage({ value, embedded = false } = {}) {
  const [localXml, setXml] = useState('');
  const xml = value ?? localXml;
  const [parsing, setParsing] = useState(false);
  const [result, setResult] = useState(null); // { ok, parsed }
  const [error, setError] = useState('');
  const [lastXml, setLastXml] = useState(''); // xml at time of last successful parse, for macro audit

  const handleParse = useCallback(async () => {
    const text = xml.trim();
    if (!text) return;
    setParsing(true);
    setError('');
    setResult(null);
    try {
      const r = await parseVastXml(text);
      setResult(r);
      setLastXml(text);
      if (r && r.ok === false) {
        setError('Proxy could not parse this XML as VAST/VMAP.');
      }
    } catch (e) {
      setError(e.message || 'Parse request failed — is the proxy reachable?');
    } finally {
      setParsing(false);
    }
  }, [xml]);

  const macros = useMemo(() => auditMacros(lastXml), [lastXml]);

  const parsed = result?.parsed;
  const ads = useMemo(() => getAds(parsed), [parsed]);
  const wrapperChain = useMemo(() => getWrapperChain(parsed), [parsed]);

  return (
    <div style={{ padding: embedded ? '8px 0' : 20, display: 'grid', gap: 16, maxWidth: embedded ? 'none' : 1200, margin: embedded ? 0 : '0 auto' }}>
      {!embedded && (
      <div>
        <h2 style={{ margin: 0, color: 'var(--ink)' }}>VAST Inspector</h2>
        <p style={{ margin: '4px 0 0 0', color: 'var(--muted)', fontSize: 13 }}>
          Paste any VAST or VMAP XML payload to see the ad system, wrapper chain,
          impression/tracking-event inventory, media files, and a scan for
          unsubstituted macro tokens (e.g. <code>[TIMESTAMP]</code>, <code>%%CACHEBUSTER%%</code>).
        </p>
      </div>
      )}

      <section style={card}>
        {!embedded && <>
        <h3 style={sectionTitle}>Paste VAST/VMAP XML</h3>
        <textarea
          value={xml}
          onChange={(e) => setXml(e.target.value)}
          placeholder="<VAST version=&quot;4.0&quot;>...</VAST>"
          rows={10}
          style={textarea}
        />
        </>}
        <div style={{ display: 'flex', gap: 8, marginTop: embedded ? 0 : 10, alignItems: 'center' }}>
          <button onClick={handleParse} disabled={parsing || !xml.trim()} style={btnPrimary}>
            {parsing ? 'Parsing…' : 'Parse'}
          </button>
          {result && result.ok !== false && (
            <CopyButton getText={() => JSON.stringify(result, null, 2)} />
          )}
        </div>
        {error && <div style={errorNote}>{error}</div>}
      </section>

      {result && result.ok !== false && (
        <>
          {/* Ad system / id / title */}
          <section style={card}>
            <h3 style={sectionTitle}>Ad{ads.length > 1 ? 's' : ''} {ads.length ? `(${ads.length})` : ''}</h3>
            {ads.length === 0 ? (
              <div style={hintNote}>No ad entries found in the parsed response — check the raw JSON via Copy.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {ads.map((ad, i) => (
                  <div key={i} style={subCard}>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12 }}>
                      <Field label="Ad System" value={ad.adSystem?.name || ad.adSystem || '—'} />
                      <Field label="Ad ID" value={ad.adId || ad.id || '—'} />
                      <Field label="Sequence" value={ad.sequence ?? '—'} />
                      <Field label="Title" value={ad.adTitle || ad.title || '—'} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Wrapper chain */}
          {wrapperChain.length > 0 && (
            <section style={card}>
              <h3 style={sectionTitle}>Wrapper chain ({wrapperChain.length})</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {wrapperChain.map((w, i) => (
                  <div key={i} style={{ ...subCard, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)' }}>#{i + 1}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ink)', wordBreak: 'break-all' }}>
                      {typeof w === 'string' ? w : w.url || w.uri || JSON.stringify(w)}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Impressions + tracking events, per ad */}
          <section style={card}>
            <h3 style={sectionTitle}>Impression &amp; tracking events</h3>
            {ads.length === 0 ? (
              <div style={hintNote}>No ad entries to inspect.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {ads.map((ad, i) => {
                  const impressions = getImpressions(ad);
                  const grouped = flattenTrackingEvents(ad);
                  const eventKeys = Object.keys(grouped);
                  return (
                    <div key={i} style={subCard}>
                      {ads.length > 1 && (
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 6 }}>Ad #{i + 1}</div>
                      )}
                      <div style={{ marginBottom: 8 }}>
                        <span style={pillLabel}>impression</span>
                        <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 6 }}>{impressions.length} tracker{impressions.length === 1 ? '' : 's'}</span>
                        {impressions.length === 0 && (
                          <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>⚠ no impression trackers found</div>
                        )}
                      </div>
                      {eventKeys.length === 0 ? (
                        <div style={{ fontSize: 12, color: 'var(--muted)' }}>No tracking events found.</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {eventKeys.map((event) => (
                            <div key={event}>
                              <span style={pillLabel}>{event}</span>
                              <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 6 }}>
                                {grouped[event].length} url{grouped[event].length === 1 ? '' : 's'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Media files */}
          <section style={card}>
            <h3 style={sectionTitle}>Media files</h3>
            {ads.every((ad) => getMediaFiles(ad).length === 0) ? (
              <div style={hintNote}>No media files found in the parsed response.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={table}>
                  <thead>
                    <tr>
                      <th style={th}>Type</th>
                      <th style={th}>Bitrate</th>
                      <th style={th}>Size</th>
                      <th style={th}>URL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ads.flatMap((ad, adI) => getMediaFiles(ad).map((mf, i) => (
                      <tr key={`${adI}-${i}`}>
                        <td style={td}>{mf.type || mf.mimeType || mf.contentType || '—'}</td>
                        <td style={td}>{mf.bitrate ?? '—'}</td>
                        <td style={td}>{mf.width && mf.height ? `${mf.width}x${mf.height}` : (mf.size ?? '—')}</td>
                        <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }} title={mf.url || mf.uri || ''}>
                          {truncateUrl(mf.url || mf.uri || '')}
                        </td>
                      </tr>
                    )))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Macro audit — scans the RAW pasted XML, not the parsed object */}
          <section style={card}>
            <h3 style={sectionTitle}>Macro audit ({macros.length} distinct)</h3>
            <p style={{ margin: '0 0 10px 0', color: 'var(--muted)', fontSize: 12 }}>
              Scans the raw pasted XML for <code>[MACRO]</code> / <code>%%MACRO%%</code>-style tokens.
              Any macro still present here at this stage means the SSP/ad server did not substitute it
              before delivery.
            </p>
            {macros.length === 0 ? (
              <div style={hintNote}>No unsubstituted macro tokens found.</div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {macros.map(({ token, count }) => (
                  <span key={token} style={{ ...chip, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {token}
                    <span style={{ background: 'var(--line)', borderRadius: 8, padding: '0 6px', fontSize: 10 }}>{count}</span>
                  </span>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ color: 'var(--muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ color: 'var(--ink)', fontFamily: 'monospace' }}>{String(value)}</div>
    </div>
  );
}

// ── styles ─────────────────────────────────────────────────────────────────
const card = {
  border: '1px solid var(--line)',
  borderRadius: 10,
  padding: 16,
  background: 'var(--surface)',
};
const subCard = {
  border: '1px solid var(--line)',
  borderRadius: 8,
  padding: 10,
  background: 'var(--bg)',
};
const sectionTitle = { margin: '0 0 10px 0', color: 'var(--ink)', fontSize: 15 };
const textarea = {
  width: '100%', boxSizing: 'border-box', padding: 10, fontSize: 12, fontFamily: 'monospace',
  borderRadius: 8, border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--ink)', resize: 'vertical',
};
const btnPrimary = {
  padding: '8px 16px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: 'none',
  background: '#3b82f6', color: '#fff', cursor: 'pointer',
};
const btnGhost = {
  padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 8,
  border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink)', cursor: 'pointer',
};
const errorNote = {
  marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: 12,
  color: '#ef4444', background: '#ef444414', border: '1px solid #ef444455',
};
const hintNote = {
  padding: '8px 12px', borderRadius: 8, fontSize: 12,
  color: 'var(--muted)', background: 'var(--bg)', border: '1px dashed var(--line)',
};
const pillLabel = {
  fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
  background: '#3b82f622', color: '#3b82f6', textTransform: 'uppercase', letterSpacing: 0.3,
};
const chip = {
  fontSize: 11, padding: '3px 9px', borderRadius: 10, background: 'var(--bg)',
  border: '1px solid var(--line)', color: 'var(--ink)', fontFamily: 'monospace',
};
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 12 };
const th = {
  textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--line)',
  color: 'var(--muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3,
};
const td = { padding: '6px 8px', borderBottom: '1px solid var(--line)', color: 'var(--ink)' };
